import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Pressable,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { GoalsSkeleton } from '../../components/Skeleton';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, PlusIcon, EditIcon, TrashIcon, XIcon } from '../../components/Icons';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { servicesApi, goalsApi, organizationalUnitsApi } from '../../services/apiService';
import { useServicesQuery } from '../../hooks/queries';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';
import { queryKeys } from '../../providers/QueryProvider';
import { ServiceList, StrategicGoalList, OrganizationalUnitList } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useResponsive, useRoleNavigation } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components (remaining non-shared icons)
// ============================================================

const MoreVerticalIcon = ({ size = 16, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);


// ============================================================
// Achievement Color Helper
// ============================================================

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return '#22C55E';  // Green - Excellent
  if (percentage >= 80) return '#F97316';   // Orange - Good
  return '#EF4444';                          // Red - Needs Improvement
};

// ============================================================
// Progress Ring Component
// ============================================================

interface ProgressRingProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;  // Optional color override
  trackColor?: string;
  textColor?: string;
}

const ProgressRing = ({ percentage, size = 80, strokeWidth = 6, color, trackColor = '#E2E8F0', textColor = '#414042' }: ProgressRingProps) => {
  const { colors, isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = color || getAchievementColor(percentage);

  // Theme-aware colors
  const bgTrackColor = trackColor || (isDark ? '#374151' : '#E2E8F0');
  const dotStrokeColor = isDark ? colors.card : '#FCFCFC';

  // Calculate dot position at the end of progress arc
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background track */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={bgTrackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot indicator at end of progress */}
        <Circle
          cx={dotX}
          cy={dotY}
          r={6}
          fill={progressColor}
          stroke={dotStrokeColor}
          strokeWidth={2}
        />
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: 16, fontFamily: FONTS.numbers, fontWeight: '500', color: textColor }}>
          %{formatPct(percentage)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
}

const Dropdown = ({ value, options, placeholder, onChange }: DropdownProps) => {
  const { colors, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    buttonBg: colors.card,
    buttonBorder: colors.cardBorder,
    buttonText: colors.textPrimary,
    placeholderText: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.cardBorder,
    itemBorder: isDark ? colors.border : '#F1F5F9',
    itemText: colors.textPrimary,
    iconColor: colors.textMuted,
  };

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        style={[styles.dropdownButton, { backgroundColor: dropdownTheme.buttonBg, borderColor: dropdownTheme.buttonBorder }]}
        onPress={() => setIsOpen(!isOpen)}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.iconColor} />
        <Text style={[styles.dropdownButtonText, { color: dropdownTheme.buttonText }, !selectedOption && { color: dropdownTheme.placeholderText }]} numberOfLines={1}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {isOpen && (
        <>
          <Pressable style={styles.dropdownOverlay} onPress={() => setIsOpen(false)} />
          <View style={[styles.dropdownMenu, { backgroundColor: dropdownTheme.menuBg, borderColor: dropdownTheme.menuBorder }]}>
            <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
              <TouchableOpacity
                style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
                onPress={() => {
                  onChange('');
                  setIsOpen(false);
                }}
              >
                <Text style={[styles.dropdownItemText, { color: dropdownTheme.itemText }]} numberOfLines={1}>{placeholder}</Text>
              </TouchableOpacity>
              {options.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
                  onPress={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  <Text style={[styles.dropdownItemText, { color: dropdownTheme.itemText }]} numberOfLines={1}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </>
      )}
    </View>
  );
};

// ============================================================
// Service Card Component
// ============================================================

interface ServiceCardProps {
  service: ServiceList;
  onPress: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMenuToggle: () => void;
  isMenuOpen: boolean;
  isViewOnly?: boolean;
  colors?: {
    card: string;
    cardBorder: string;
    textPrimary: string;
    textMuted: string;
    iconDefault: string;
    border: string;
  };
  isDark?: boolean;
  translations?: {
    strategicGoal: string;
    viewDetails: string;
    edit: string;
    delete: string;
  };
}

const ServiceCard = ({ service, onPress, onEdit, onDelete, onMenuToggle, isMenuOpen, isViewOnly, colors, isDark, translations }: ServiceCardProps) => {
  // Use real achievement percentage from API (default to 0 if not available)
  const percentage = service.achievement_percentage ?? 0;
  const isPending = service.approval_status === 'pending';

  // Theme colors with defaults
  const cardBg = colors?.card || '#FFFFFF';
  const cardBorderColor = colors?.cardBorder || '#E2E8F0';
  const textPrimary = colors?.textPrimary || '#414042';
  const textMuted = colors?.textMuted || '#6B7280';
  const iconColor = colors?.iconDefault || '#414042';
  const trackColor = colors?.border || '#E5E7EB';
  const goalSectionBg = isDark ? 'rgba(255,255,255,0.1)' : '#F1F5F9';

  const handleEdit = () => {
    onEdit();
  };

  const handleDelete = () => {
    onDelete();
  };

  return (
    <View style={[styles.serviceCard, { flex: 1, backgroundColor: cardBg, borderColor: cardBorderColor }]}>
      {/* Header Row - Content on right (first in RTL), Progress ring on left (second in RTL) */}
      <View style={styles.serviceCardHeader}>
        {/* Title Column - contains title/badge section + description */}
        <View style={styles.serviceCardTitleColumn}>
          {/* Title + Badge Section */}
          <View style={styles.serviceCardTitleSection}>
            <Text style={[styles.serviceCardTitle, { color: textPrimary }]} numberOfLines={1}>{service.name}</Text>
            {isPending && (
              <View style={styles.pendingBadge}>
                <Text style={styles.pendingBadgeText}>{translations?.pendingApproval || 'بانتظار الموافقة'}</Text>
              </View>
            )}
            {service.has_pending_deletion && <PendingDeletionBadge compact />}
            {service.owning_unit_name && (
              <View style={styles.unitBadge}>
                <Text style={styles.unitBadgeText} numberOfLines={1}>{service.owning_unit_name}</Text>
              </View>
            )}
          </View>
          {/* Description - inside title column */}
          <Text style={[styles.serviceDescription, { color: textMuted }]} numberOfLines={2}>
            {service.name}
          </Text>
        </View>
        <ProgressRing percentage={percentage} size={80} strokeWidth={6} trackColor={trackColor} textColor={textPrimary} />
      </View>

      {/* Strategic Goal Section */}
      {service.strategic_goal_title && (
        <View style={styles.goalSectionWrapper}>
          <View style={[styles.goalSection, { backgroundColor: goalSectionBg }]}>
            <Text style={[styles.goalLabel, { color: textMuted }]}>{translations?.strategicGoal}</Text>
            <Text style={[styles.goalValue, { color: textPrimary }]} numberOfLines={1}>{service.strategic_goal_title}</Text>
          </View>
        </View>
      )}

      {/* Spacer to push footer to bottom */}
      <View style={{ flex: 1 }} />

      {/* Footer - Details link on left, Menu on right */}
      <View style={styles.serviceCardFooter}>
        <TouchableOpacity style={styles.detailsLink} onPress={onPress}>
          <Text style={[styles.detailsLinkText, { color: textMuted }]}>{translations?.viewDetails}</Text>
          <ChevronLeftIcon size={16} color={iconColor} />
        </TouchableOpacity>
        {/* Hide menu for view-only users (org_executive, executive, strategy_team) */}
        {!isViewOnly && (
          <View style={styles.menuContainer}>
            <TouchableOpacity style={styles.menuButton} onPress={onMenuToggle}>
              <MoreVerticalIcon size={20} color={iconColor} />
            </TouchableOpacity>
            {isMenuOpen && (
            <>
              <Pressable style={styles.menuOverlay} onPress={onMenuToggle} />
              <View style={[styles.cardDropdownMenu, { backgroundColor: cardBg, borderColor: cardBorderColor }]}>
                <TouchableOpacity style={styles.cardDropdownItem} onPress={handleEdit}>
                  <Text style={[styles.cardDropdownItemText, { color: textPrimary }]}>{translations?.edit}</Text>
                  <EditIcon size={16} color={iconColor} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.cardDropdownItemDelete} onPress={handleDelete}>
                  <Text style={styles.cardDropdownItemTextDelete}>{translations?.delete}</Text>
                  <TrashIcon size={16} color="#EF4444" />
                </TouchableOpacity>
              </View>
            </>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

type RouteProps = RouteProp<MainStackParamList, 'Services'>;

export default function ServicesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const organizationName = route.params?.organizationName;
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors object to pass to cards
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
  };

  // Check if user is org_employee (limited permissions)
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';

  // Filter state
  const [selectedGoal, setSelectedGoal] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');

  // Menu state - lifted to parent for z-index management
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);

  // Delete confirmation modal state
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<ServiceList | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  const queryClient = useQueryClient();

  // Main data query — React Query renders cached data instantly on focus.
  const {
    data: servicesData,
    isLoading: loading,
    refetch,
  } = useServicesQuery({
    page: currentPage,
    pageSize,
    department_id: effectiveOrgId,
    strategic_goal: selectedGoal ? Number(selectedGoal) : undefined,
    organizational_unit: selectedUnit ? Number(selectedUnit) : undefined,
    year: selectedYear,
  });

  const services: ServiceList[] = useMemo(
    () => servicesData?.results || [],
    [servicesData],
  );
  const totalCount = servicesData?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Filter dropdowns (goals + org units) — separate queries, rarely change.
  const { data: goalsDropdownData } = useQuery({
    queryKey: ['goals', 'dropdown', effectiveOrgId ?? null],
    queryFn: async () => {
      const goalsParams: Record<string, any> = { page_size: 100 };
      if (effectiveOrgId) goalsParams.department_id = effectiveOrgId;
      return goalsApi.list(goalsParams);
    },
    staleTime: 10 * 60 * 1000,
  });
  const { data: unitsDropdownData } = useQuery({
    queryKey: ['organizationalUnits', 'dropdown', effectiveOrgId ?? null],
    queryFn: async () => {
      const unitsParams: Record<string, any> = { page_size: 100 };
      if (effectiveOrgId) unitsParams.department_id = effectiveOrgId;
      return organizationalUnitsApi.list(unitsParams);
    },
    staleTime: 10 * 60 * 1000,
  });

  const goals: DropdownOption[] = useMemo(
    () =>
      (goalsDropdownData?.results || []).map((g: StrategicGoalList) => ({
        value: g.id.toString(),
        label: translate(g.title),
      })),
    [goalsDropdownData, translate],
  );
  const units: DropdownOption[] = useMemo(
    () =>
      (unitsDropdownData?.results || []).map((u: OrganizationalUnitList) => ({
        value: u.id.toString(),
        label: translate(u.name_ar || u.name),
      })),
    [unitsDropdownData, translate],
  );

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  // Back-compat for handlers that called fetchServices() — invalidates
  // the React Query cache so the next access triggers a refetch.
  const fetchServices = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
  }, [queryClient]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setSelectedGoal('');
    setSelectedUnit('');
    setCurrentPage(1);
  };

  const handleAddService = () => {
    navigation.navigate('AddService', {});
  };

  const handleToggleMenu = (serviceId: number) => {
    setActiveMenuId(activeMenuId === serviceId ? null : serviceId);
  };

  const handleEditService = (service: ServiceList) => {
    setActiveMenuId(null);
    navigation.navigate('AddService', { serviceId: service.id });
  };

  const handleDeletePress = (service: ServiceList) => {
    setActiveMenuId(null);
    setServiceToDelete(service);
    setShowDeletionImpact(true);
  };

  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;

    setDeleting(true);
    try {
      const response = await servicesApi.delete(serviceToDelete.id);

      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t('services.deleteSuccess'));
      }

      fetchServices(); // Refresh the list
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        fetchServices();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(error.response?.data?.detail || t('services.deleteFailed'));
      }
    } finally {
      setDeleting(false);
      setShowDeletionImpact(false);
      setServiceToDelete(null);
    }
  };

  // Full-page skeleton only on cold load.
  if (loading && services.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: containerPadding }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={organizationId ? [
            { label: t('navigation.home') },
            { label: t('nav.organizations'), route: 'Organizations' },
            { label: organizationName || '', route: 'OrganizationDetail', params: { id: organizationId } },
            { label: t('services.title') },
          ] : [
            { label: t('navigation.home') },
            { label: t('services.title') },
          ]}
        />

        {/* Header Card */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          {/* Title Row */}
          <View style={[styles.headerTitleRow, isMobile && styles.headerTitleRowMobile]}>
            <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{t('services.title')}</Text>
            {/* Hide add button for view-only users (strategy_team, executive, org_executive) */}
            {!isViewOnly && (
              <TouchableOpacity style={styles.addButton} onPress={handleAddService}>
                <Text style={styles.addButtonText}>{t('services.addService')}</Text>
                <PlusIcon size={13} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>

          {/* Filters Row */}
          <View style={[styles.filtersRow, isMobile && styles.filtersRowMobile]}>
            {/* Buttons on LEFT */}
            <View style={[styles.filterButtonsContainer, { zIndex: 1 }]}>
              {/* Clear Filter Button - Only shown when filter is active */}
              {(selectedGoal || selectedUnit) && (
                <TouchableOpacity style={styles.clearFilterButton} onPress={handleClearFilter}>
                  <XIcon size={14} color="#EF4444" />
                  <Text style={styles.clearFilterText}>{t('filters.clearFilter')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[styles.applyFilterButton, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]} onPress={handleApplyFilter}>
                <Text style={[styles.applyFilterButtonText, { color: themeColors.textPrimary }]}>{t('common.applyFilter')}</Text>
              </TouchableOpacity>
            </View>
            {/* Dropdowns on RIGHT */}
            <View style={[styles.filterDropdowns, isMobile && styles.filterDropdownsMobile, { zIndex: 100 }]}>
              <Dropdown
                value={selectedGoal}
                options={goals}
                placeholder={t('filters.allStrategicGoals')}
                onChange={setSelectedGoal}
              />
              <Dropdown
                value={selectedUnit}
                options={units}
                placeholder={t('filters.allOrganizationalUnits')}
                onChange={setSelectedUnit}
              />
              {canViewAllOrgs && (
                <OrganizationFilterDropdown
                  selectedOrgId={selectedOrgId}
                  onChange={setSelectedOrgId}
                />
              )}
            </View>
          </View>
        </View>

        {/* Services Grid */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : services.length > 0 ? (
          <View style={[styles.servicesGrid, isMobile && styles.servicesGridMobile]}>
            {services.map((service) => (
              <View
                key={service.id}
                style={[
                  styles.serviceCardWrapper,
                  isMobile && styles.serviceCardWrapperMobile,
                  activeMenuId === service.id && { zIndex: 1000, position: 'relative' }
                ]}
              >
                <ServiceCard
                  service={{
                    ...service,
                    name: translate(service.name),
                    owning_unit_name: translate(service.owning_unit_name),
                    strategic_goal_title: translate(service.strategic_goal_title),
                  }}
                  onPress={() => navigation.navigate('ServiceDetail', { id: service.id })}
                  onEdit={() => handleEditService(service)}
                  onDelete={() => handleDeletePress(service)}
                  onMenuToggle={() => handleToggleMenu(service.id)}
                  isMenuOpen={activeMenuId === service.id}
                  isViewOnly={isViewOnly}
                  colors={themeColors}
                  isDark={isDark}
                  translations={{
                    strategicGoal: t('goals.strategicGoal'),
                    viewDetails: t('common.viewDetails'),
                    edit: t('common.edit'),
                    delete: t('common.delete'),
                  }}
                />
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('services.noServices')}</Text>
            <Text style={[styles.emptySubtext, { color: colors.textMuted }]}>{t('services.addNewServiceHint')}</Text>
          </View>
        )}

        {/* Pagination - RTL layout */}
        {totalPages > 1 && (
          <View style={[styles.pagination, { borderTopColor: themeColors.cardBorder, flexDirection: rtlRow }]}>
            {/* Previous: السابق > */}
            <TouchableOpacity
              style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
              onPress={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <Text style={[styles.paginationButtonText, { color: themeColors.textMuted }]}>{t('common.previous')}</Text>
              <ChevronRightIcon size={16} color={currentPage === 1 ? themeColors.cardBorder : themeColors.textMuted} />
            </TouchableOpacity>

            <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
              {(() => {
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                const end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                return Array.from({ length: end - start + 1 }, (_, i) => start + i);
              })().map((page) => (
                <TouchableOpacity
                  key={page}
                  style={[styles.paginationNumber, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, currentPage === page && styles.paginationNumberActive]}
                  onPress={() => setCurrentPage(page)}
                >
                  <Text style={[
                    styles.paginationNumberText,
                    { color: themeColors.textMuted },
                    currentPage === page && [styles.paginationNumberTextActive, { color: themeColors.textPrimary }]
                  ]}>{page}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Next: < التالي */}
            <TouchableOpacity
              style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
              onPress={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronLeftIcon size={16} color={currentPage === totalPages ? themeColors.cardBorder : themeColors.textMuted} />
              <Text style={[styles.paginationButtonText, { color: themeColors.textMuted }]}>{t('common.next')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setServiceToDelete(null); }}
        onConfirm={handleConfirmDelete}
        entityName={serviceToDelete?.name || ''}
        fetchImpact={async () => {
          const response = await servicesApi.deletionImpact(serviceToDelete!.id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={deleting}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  // Responsive styles will override padding via inline style
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Header Card
  headerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 24,
    gap: 8,
    shadowColor: '#000000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3,
    elevation: 2,
    zIndex: 1000,
  },
  headerTitleRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  orgFilterRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginTop: 12,
    zIndex: 1100,
  },
  headerTitleRowMobile: {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: 12,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 12,
    minHeight: 44,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#f1f5f9',
  },
  // Filters
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    zIndex: 1000,
  },
  filtersRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  filterDropdowns: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  filterDropdownsMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    width: '100%',
  },
  filterButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  applyFilterButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  applyFilterButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  clearFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    backgroundColor: '#FEF2F2',
    minHeight: 44,
    gap: 6,
  },
  clearFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  // Dropdown
  dropdownContainer: {
    position: 'relative',
    minWidth: 180,
    maxWidth: 320,
    zIndex: 9999,
  },
  dropdownOverlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    minHeight: 44,
    minWidth: 180,
    gap: 8,
    shadowColor: '#000000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2,
    elevation: 1,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownPlaceholder: {
    color: '#64748B',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 50,
    zIndex: 9999,
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 18,
  },
  // Services Grid
  servicesGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'flex-start',
    overflow: 'visible',
    alignItems: 'stretch',
  },
  servicesGridMobile: {
    flexDirection: 'column',
    gap: 16,
  },
  // Service Card Wrapper
  serviceCardWrapper: {
    width: 'calc(50% - 10px)' as any,
    display: 'flex' as any,
    overflow: 'visible',
  },
  serviceCardWrapperMobile: {
    width: '100%',
    display: 'flex' as any,
  },
  // Service Card
  serviceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
    shadowColor: '#000000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3,
    elevation: 2,
    overflow: 'visible',
  },
  serviceCardHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 24,
    width: '100%',
  },
  serviceCardTitleColumn: {
    flex: 1,
    flexDirection: 'column',
    gap: 12,
    alignItems: 'flex-start',
    minHeight: 1,
    minWidth: 1,
  },
  menuButton: {
    padding: 10,
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  serviceCardTitleSection: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
    width: '100%',
  },
  serviceCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  unitBadge: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  unitBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
    lineHeight: 14,
  },
  pendingBadge: {
    backgroundColor: '#9467BD',
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  pendingBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
    lineHeight: 14,
  },
  serviceDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
    width: '100%',
  },
  goalSectionWrapper: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
  },
  goalSection: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 4,
    alignItems: 'flex-end',
  },
  goalLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
    width: '100%',
  },
  goalValue: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center',
    lineHeight: 20,
  },
  serviceCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  detailsLink: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 10,
    minHeight: 44,
    borderRadius: 4,
  },
  detailsLinkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    lineHeight: 14,
  },
  // Loading & Empty
  loadingContainer: {
    padding: 48,
    alignItems: 'center',
  },
  emptyContainer: {
    padding: 48,
    alignItems: 'center',
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#64748B',
  },
  emptySubtext: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
    textAlign: 'center',
  },
  // Pagination
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 24,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  paginationNumbers: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  paginationNumber: {
    width: 44,
    height: 44,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  paginationNumberActive: {
    backgroundColor: '#F1F5F9',
    borderColor: '#CBD5E1',
  },
  paginationNumberText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  paginationNumberTextActive: {
    color: '#414042',
    fontFamily: FONTS.medium,
  },
  // Menu Container & Dropdown
  menuContainer: {
    position: 'relative',
    zIndex: 100,
  },
  menuOverlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99,
  },
  cardDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12,
    elevation: 20,
    zIndex: 100,
    minWidth: 140,
    marginTop: 4,
    overflow: 'hidden',
  },
  cardDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  cardDropdownItemDelete: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  cardDropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  cardDropdownItemTextDelete: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
  },
  // Delete Confirmation Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    gap: 16,
  },
  modalContentMobile: {
    padding: 16,
    maxWidth: '95%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonDanger: {
    backgroundColor: '#EF4444',
  },
  modalButtonPrimary: {
    backgroundColor: '#A9211F',
  },
  modalInfoBox: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 12,
    gap: 6,
    borderWidth: 1,
    borderColor: '#FCD34D',
  },
  modalInfoTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#92400E',
    textAlign: 'right',
    marginBottom: 4,
  },
  modalInfoText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#92400E',
    textAlign: 'right',
    lineHeight: 20,
  },
  modalButtonCancel: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  modalButtonTextDanger: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  modalButtonTextCancel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
